import { useRichTextEditorContext } from '@mantine/tiptap';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { Group } from '@mantine/core';
import DropdownControl, {
  DropdownControlKind,
} from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/DropdownControl';
import SimpleControl from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/SimpleControl';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

/**
 * Control which allows the user to select font size.
 */
export default function FontSize({ kind }: { kind: DropdownControlKind }) {
  const { editor } = useRichTextEditorContext();
  const [currentSize, setCurrentSize] = useState(14);
  const { visual } = useTemplateEditorData();

  // Get current font size from editor selection
  useEffect(() => {
    if (editor) {
      const fontSize = editor.getAttributes('textStyle').fontSize;
      if (fontSize) {
        const sizeValue = parseInt(fontSize.replace('px', ''));
        if (!isNaN(sizeValue)) {
          setCurrentSize(sizeValue);
        }
      } else {
        setCurrentSize(visual.fontSizeP); // Default size
      }
    }
  }, [editor?.state.selection, visual.fontSizeP]);

  /** Apply font size using extension commands */
  const applyFontSize = (size: number) => {
    if (size < 6) size = 6;
    if (size > 200) size = 200;

    setCurrentSize(size);
    editor?.chain().focus().setFontSize(`${size}px`).run();
  };

  /** Remove font size */
  const removeFontSize = () => {
    setCurrentSize(14);
    editor?.chain().focus().unsetFontSize().run();
  };

  /** Increase font size */
  const increaseFontSize = () => {
    applyFontSize(currentSize + 1);
  };

  /** Decrease font size */
  const decreaseFontSize = () => {
    applyFontSize(currentSize - 1);
  };

  /** Handle preset size click */
  const presetSizeClicked = (size: number) => {
    if (currentSize === size) {
      removeFontSize();
    } else {
      applyFontSize(size);
    }
  };

  return (
    <Group spacing={2}>
      {/* Decrease button */}
      <SimpleControl
        kind="action-icon"
        icon={<IconMinus size={16} />}
        label="Zmenšiť písmo"
        onClick={decreaseFontSize}
      />

      {/* Font size dropdown */}

      <DropdownControl kind={kind} label="Veľkosť písma" text={`${currentSize}px`} hideChevron>
        {/* Preset sizes */}
        {FONT_SIZES.map((size) => (
          <SimpleControl
            key={`FontSize.${size}`}
            kind="menu-item"
            icon={null}
            label={`${size}px`}
            onClick={() => presetSizeClicked(size)}
            active={currentSize === size}
            minWidth={50}
          />
        ))}
      </DropdownControl>

      {/* Increase button */}
      <SimpleControl
        kind="action-icon"
        icon={<IconPlus size={16} />}
        label="Zväčšiť písmo"
        onClick={increaseFontSize}
      />
    </Group>
  );
}
